import { useState } from "react"
import {
    Alert,
    Grid,
    Link,
    Stack,
    StackItem,
    Tooltip,
    Typography,
} from "@octopus-energy/coral/atoms"
import { FormikTextField } from "@octopus-energy/coral/formik"
import { IconCircleInformationOutlined } from "@octopus-energy/icons"
import { useIsOpen } from "@oefr/hooks"
import { ZodFormik } from "@oefr/ui"
import { formatStreetName, formatStreetNumber } from "@oefr/utils/string"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslations } from "next-intl"
import { z } from "zod"
import { AddressField } from "@/components/molecules"
import { FormActions } from "@/components/organisms"
import { MarketName } from "@/constants/marketName"
import { onboardingDataLayer, OnboardingDataLayerEvent } from "@/features/onboarding/datalayer"
import { type OnboardingState } from "@/features/onboarding/wizard"
import { EnergyType, SubscriptionContainer } from "@/features/subscription"
import { EligibilityLoadingModal } from "@/features/subscription/meterEligibility"
import { graphql, type InputOf } from "@/graphql"
import { useGraphQLClient } from "@/lib/blueprint-auth/client"
import { OnboardingRouteNames } from "@/routes/onboarding"
import { useInitialOnboardingFormContext } from "../../context/InitialOnboardingFormContext"
import { useEligibilityCheckQuote } from "../../hooks/useEligibilityCheckQuote"

const ADDRESS_LOOKUP_FOUND_EVENT = {
    [EnergyType.Electricity]: OnboardingDataLayerEvent.AddressLookupPRMmeterFound,
    [EnergyType.Gas]: OnboardingDataLayerEvent.AddressLookupPCEmeterFound,
}

const ADDRESS_LOOKUP_NOT_FOUND_EVENT = {
    [EnergyType.Electricity]: OnboardingDataLayerEvent.AddressLookupPRMmeterNotFound,
    [EnergyType.Gas]: OnboardingDataLayerEvent.AddressLookupPCEmeterNotFound,
}

const MeterPointNumberByAdressQuery = graphql(`
    query MeterPointNumberByAdress($input: MeterPointNumberByAddressInputType!) {
        meterPointNumberByAddress(input: $input)
    }
`)

function useAddressLookupFormSchema() {
    const t = useTranslations("AddressLookupForm")

    const getAddressLookupFormSchema = () =>
        z
            .object({
                user: z.object({
                    givenName: z.string({ error: t("schema.givenNameError") }),
                    familyName: z.string({ error: t("schema.familyNameError") }),
                }),
                address: z.object({
                    addrCityLabel: z.string({ error: t("schema.addrCityLabelError") }),
                    addrNumStreet: z.string({ error: t("schema.addrNumStreetError") }),
                    addrPostCode: z.string({ error: t("schema.addrPostCodeError") }),
                }),
            })
            .transform((data) => ({
                ...data.user,
                finalUserName: data.user.familyName.toUpperCase(),
                streetNumber: formatStreetNumber(data.address.addrNumStreet),
                streetName: formatStreetName(data.address.addrNumStreet),
                postcode: data.address.addrPostCode,
                city: data.address.addrCityLabel.replaceAll("-", " ").toUpperCase(),
            }))

    return getAddressLookupFormSchema
}

export function AddressLookupForm() {
    const t = useTranslations("AddressLookupForm")

    const [meterNotFound, setMeterNotFound] = useState<boolean>(false)

    const { isOpen: isCustomAddressFormOpen, open: openCustomAddressForm } = useIsOpen()

    const {
        givenName,
        familyName,
        journeyType,
        onboardingState,
        selectedEnergyTypes,
        setInputMethod,
        setMeterPoints,
    } = useInitialOnboardingFormContext()

    const { validateMeterPoints } = useEligibilityCheckQuote({
        stepName: OnboardingRouteNames.MeterDetails,
    })

    const getFormSchema = useAddressLookupFormSchema()

    const queryClient = useQueryClient()
    const graphQLClient = useGraphQLClient()

    const getMeterPointNumberByAddress = async (
        input: InputOf<typeof MeterPointNumberByAdressQuery>
    ) => {
        try {
            const { meterPointNumberByAddress } = await queryClient.fetchQuery({
                queryKey: ["MeterPointNumberByAdressQuery"],
                queryFn() {
                    return graphQLClient.request(MeterPointNumberByAdressQuery, { input })
                },
            })

            return meterPointNumberByAddress
        } catch {
            return null
        }
    }

    const handleSubmit = async (values: z.infer<ReturnType<typeof getFormSchema>>) => {
        setMeterNotFound(false)

        const newOnboardingState: OnboardingState = {
            formValues: {
                ...structuredClone(onboardingState.formValues),
                journeyType,
                selectedEnergyTypes,
            },
            stepHistory: [OnboardingRouteNames.MeterDetails],
            submittedSteps: [],
        }

        onboardingDataLayer.sendTrackingEvent(
            OnboardingDataLayerEvent.AddressLookupFormSent,
            newOnboardingState.formValues
        )

        const meterPoints = []

        for (const energyType of selectedEnergyTypes) {
            const { marketName } = newOnboardingState.formValues[energyType]
            const isMarketElectricity = marketName === MarketName.FRA_ELECTRICITY

            const input = {
                marketName,
                finalUserName: values.finalUserName,
                streetNumber: values.streetNumber,
                streetName: values.streetName,
                postcode: values.postcode,
                city: values.city,
            }

            let meterPointId = await getMeterPointNumberByAddress({
                ...input,
                outOfSupplierPerimeter: isMarketElectricity,
            })

            // ElecOnly: Lookup for address in our perimeter if not found out of supplier perimeter
            if (!meterPointId && isMarketElectricity) {
                meterPointId = await getMeterPointNumberByAddress(input)
            }

            if (!!meterPointId) {
                onboardingDataLayer.sendTrackingEvent(
                    ADDRESS_LOOKUP_FOUND_EVENT[energyType],
                    newOnboardingState.formValues
                )

                meterPoints.push({
                    meterPointId,
                    marketName,
                    journeyType,
                    givenName: values.givenName,
                    familyName: values.familyName,
                })
            } else {
                onboardingDataLayer.sendTrackingEvent(
                    ADDRESS_LOOKUP_NOT_FOUND_EVENT[energyType],
                    newOnboardingState.formValues
                )
            }
        }

        if (meterPoints.length === 0) {
            return setMeterNotFound(true)
        }

        if (meterPoints.length !== selectedEnergyTypes.length) {
            setInputMethod("meterPoint")
            setMeterPoints(meterPoints)

            return
        }

        onboardingDataLayer.sendTrackingEvent(
            OnboardingDataLayerEvent.AddressLookupAllMeterFound,
            newOnboardingState.formValues
        )

        await validateMeterPoints(meterPoints, newOnboardingState)
    }

    return (
        <ZodFormik
            initialValues={{
                address: {
                    addrNumStreet: "",
                    addrPostCode: "",
                    addrCityLabel: "",
                },
                user: {
                    givenName,
                    familyName,
                },
            }}
            onSubmit={handleSubmit}
            validationSchema={getFormSchema()}
        >
            {({ isSubmitting }) => (
                <>
                    <EligibilityLoadingModal isLoading={isSubmitting} />
                    <SubscriptionContainer.Form gap="md">
                        <Stack gap="sm">
                            <Typography
                                textStyle="h4"
                                component="p"
                            >
                                {t("title")}
                            </Typography>
                            <Tooltip title={t("tooltip")}>
                                <IconCircleInformationOutlined />
                            </Tooltip>
                        </Stack>

                        {isCustomAddressFormOpen ? (
                            <>
                                <FormikTextField
                                    label={t("input.numberAddressLabel")}
                                    name="address.addrNumStreet"
                                    autocomplete="address-line1"
                                />
                                <Stack flexDirection={{ base: "column", md: "row" }}>
                                    <StackItem flex="1">
                                        <FormikTextField
                                            label={t("input.zipcodeLabel")}
                                            name="address.addrPostCode"
                                            autocomplete="postal-code"
                                        />
                                    </StackItem>
                                    <StackItem flex="2">
                                        <FormikTextField
                                            label={t("input.cityLabel")}
                                            name="address.addrCityLabel"
                                            autocomplete="address-level2"
                                        />
                                    </StackItem>
                                </Stack>
                            </>
                        ) : (
                            <AddressField
                                label={t("input.numberAddressLabel")}
                                fieldName="address"
                                onOptionSelect={openCustomAddressForm}
                            />
                        )}
                        <Grid
                            gap="md"
                            gridTemplateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
                        >
                            <FormikTextField
                                attributes={{ "data-testid": "givenName" }}
                                label={t("input.user.firstName")}
                                name="user.givenName"
                                autocomplete="given-name"
                            />
                            <FormikTextField
                                attributes={{ "data-testid": "familyName" }}
                                label={t("input.user.lastName")}
                                name="user.familyName"
                                autocomplete="family-name"
                            />
                        </Grid>

                        {!isCustomAddressFormOpen && (
                            <Link onClick={openCustomAddressForm}>{t("addressNotFoundable")}</Link>
                        )}

                        <FormActions step>
                            {meterNotFound && (
                                <Alert severity="warning">
                                    <Typography color="inherit">{t("noMeterFound")}</Typography>
                                </Alert>
                            )}
                            <FormActions.Accept>{t("cta")}</FormActions.Accept>
                            <Typography textStyle="caption">
                                {t("legal", {
                                    energyType:
                                        selectedEnergyTypes.length > 1
                                            ? "dual"
                                            : selectedEnergyTypes[0],
                                })}
                            </Typography>
                        </FormActions>
                    </SubscriptionContainer.Form>
                </>
            )}
        </ZodFormik>
    )
}

AddressLookupForm.messages = [
    "AddressLookupForm",
    ...AddressField.messages,
    ...EligibilityLoadingModal.messages,
] as const
